// Results page: renders the aggregates from GET /api/results. See SPEC.md "results.html"
// and server/results.mjs for the response shape.
import { getKey, requireKeyOrExplain, fmtMs, fmtMbps, fmtPct, CARRIERS } from '../common.js';
import { FAIL_STAGES, FAIL_STAGE_LABELS } from '../netdiag.js';

const $ = (id) => document.getElementById(id);

const BUCKETS = [
  { id: 'evening', label: 'المساء 18–24' },
  { id: 'other', label: 'باقي اليوم' },
  { id: 'all', label: 'الكلّ' },
];

const S = { loaded: false, data: null };
Object.defineProperty(window, '__lab', {
  configurable: false,
  enumerable: true,
  get: () => ({ loaded: S.loaded, data: S.data ? JSON.parse(JSON.stringify(S.data)) : null }),
});

let busy = false;

// ---------- helpers ----------

function el(tag, cls, text) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined && text !== null) e.textContent = String(text);
  return e;
}

function show(node, on = true) {
  if (node) node.classList.toggle('hidden', !on);
}

const isNum = (x) => typeof x === 'number' && Number.isFinite(x);
const fmtNum = (x) => (isNum(x) ? (Math.abs(x) >= 10 ? String(Math.round(x)) : x.toFixed(1)) : '—');
const fmtKbps = (x) => (isNum(x) ? `${Math.round(x)} kbps` : '—');

// Stat cell "p50 (p95)" from a { n, p50, p95 } summary.
function statCell(stat, fmt, withP95 = true) {
  const td = el('td');
  if (!stat || !isNum(stat.p50)) {
    td.textContent = '—';
    return td;
  }
  td.append(document.createTextNode(fmt(stat.p50)));
  if (withP95 && isNum(stat.p95) && stat.n > 1) {
    td.append(document.createTextNode(' '));
    td.append(el('span', 'muted', `(${fmt(stat.p95)})`));
  }
  return td;
}

function textCell(text) {
  return el('td', null, text == null || text === '' ? '—' : text);
}

// "92% من 13": share of the sessions given IPv6 that used it (ipv6WorksPct of ipv6Tried).
function worksCell(pct, tried) {
  const td = el('td');
  if (!isNum(pct) || !(tried > 0)) {
    td.textContent = '—';
    return td;
  }
  td.append(document.createTextNode(fmtPct(pct)), document.createTextNode(' '), el('span', 'muted', `من ${tried}`));
  return td;
}

function carrierLabel(id) {
  return CARRIERS.find((c) => c.id === id)?.label ?? id;
}

function carriersWithData(kindData) {
  if (!kindData || typeof kindData !== 'object') return [];
  const known = CARRIERS.map((c) => c.id).filter((id) => kindData[id]);
  const extra = Object.keys(kindData).filter((id) => !known.includes(id));
  return [...known, ...extra];
}

// One table per carrier: rows = buckets, columns = defs [{ head, cell(bucket) }].
function buildTable(caption, perBucket, defs) {
  const table = el('table', 'results');
  table.append(el('caption', null, caption));
  const thead = el('thead');
  const hr = el('tr');
  hr.append(el('th', null, 'الفترة'));
  hr.append(el('th', null, 'العدد'));
  for (const d of defs) hr.append(el('th', null, d.head));
  thead.append(hr);
  table.append(thead);

  const tbody = el('tbody');
  for (const b of BUCKETS) {
    const bucket = perBucket?.[b.id];
    const tr = el('tr');
    const th = el('th', null, b.label);
    th.scope = 'row';
    tr.append(th);
    const n = bucket && isNum(bucket.n) ? bucket.n : 0;
    tr.append(el('td', null, String(n)));
    for (const d of defs) {
      tr.append(n > 0 ? d.cell(bucket, bucket.fields || {}) : textCell(null));
    }
    tbody.append(tr);
  }
  table.append(tbody);
  return table;
}

// `extra(id, perBucket)` may return one more element to show under the carrier's table.
function renderKind(container, kindData, defs, emptyText, extra) {
  container.replaceChildren();
  const ids = carriersWithData(kindData);
  if (!ids.length) {
    container.append(el('p', 'muted', emptyText));
    return;
  }
  for (const id of ids) {
    container.append(buildTable(carrierLabel(id), kindData[id], defs));
    const more = extra ? extra(id, kindData[id]) : null;
    if (more) container.append(more);
  }
}

// ---------- by address family (nettest byFamily / byHttpFamily) ----------

const FAMILIES = [
  { id: 'ipv4', label: 'IPv4' },
  { id: 'ipv6', label: 'IPv6' },
];

const UDP_FAMILY_TABLE = {
  key: 'byFamily',
  title: 'UDP حسب نوع العنوان',
  heads: ['الوسيط', 'p95', 'الفقد'],
  // Count = runs behind the medians (UDP only); ICE-TCP runs of that family are shown apart.
  count: (fam) => {
    const udp = fam?.fields?.['udp.p50']?.n ?? 0;
    const tcp = (fam?.n ?? 0) - udp;
    const td = el('td', null, String(udp));
    if (tcp > 0) td.append(document.createTextNode(' '), el('span', 'muted', `+${tcp} TCP`));
    return td;
  },
  cells: (f) => [
    statCell(f['udp.p50'], fmtMs, false),
    statCell(f['udp.p95'], fmtMs, false),
    statCell(f['udp.lossPct'], fmtPct, false),
  ],
};

const HTTP_FAMILY_TABLE = {
  key: 'byHttpFamily',
  title: 'السرعة حسب نوع العنوان',
  heads: ['تنزيل', 'رفع', 'زمن HTTP'],
  count: (fam) => el('td', null, String(fam?.n ?? 0)),
  cells: (f) => [
    statCell(f['down.mbps'], fmtMbps, false),
    statCell(f['up.mbps'], fmtMbps, false),
    statCell(f['http.p50'], fmtMs, false),
  ],
};

// Per bucket: a heading row, then one row per family, with few narrow columns (median over
// the runs only), so the table fits a 375 px screen. Whether IPv6 works is in the main table.
function buildFamilyTable(id, perBucket, spec) {
  const famOf = (b, f) => perBucket?.[b.id]?.[spec.key]?.[f.id];
  const has = BUCKETS.some((b) => FAMILIES.some((f) => famOf(b, f)?.n > 0));
  if (!has) return null;
  const heads = ['العنوان', 'العدد', ...spec.heads];
  const table = el('table', 'results');
  table.append(el('caption', null, `${carrierLabel(id)}: ${spec.title}`));
  const thead = el('thead');
  const hr = el('tr');
  for (const h of heads) hr.append(el('th', null, h));
  thead.append(hr);
  table.append(thead);
  const tbody = el('tbody');
  for (const b of BUCKETS) {
    const gr = el('tr');
    const gh = el('th', 'muted', b.label);
    gh.colSpan = heads.length;
    gh.scope = 'colgroup';
    gr.append(gh);
    tbody.append(gr);
    for (const f of FAMILIES) {
      const fam = famOf(b, f);
      const tr = el('tr');
      const th = el('th', null, f.label);
      th.scope = 'row';
      tr.append(th, spec.count(fam), ...spec.cells((fam?.n > 0 && fam.fields) || {}));
      tbody.append(tr);
    }
  }
  table.append(tbody);
  return table;
}

function buildFamilyTables(id, perBucket) {
  const tables = [UDP_FAMILY_TABLE, HTTP_FAMILY_TABLE].map((spec) => buildFamilyTable(id, perBucket, spec)).filter(Boolean);
  if (!tables.length) return null;
  const frag = document.createDocumentFragment();
  frag.append(...tables);
  return frag;
}

// «أسباب تعذّر UDP: لم يكتمل الاتّصال (الإرسال) 3 · …» from the 'all' bucket's udpFailStages
// (the step where failed echo runs stopped); null when there is none.
function buildFailLine(perBucket) {
  const counts = perBucket?.all?.udpFailStages;
  if (!counts || typeof counts !== 'object') return null;
  const parts = FAIL_STAGES.filter((s) => isNum(counts[s]) && counts[s] > 0).map((s) => `${FAIL_STAGE_LABELS[s]} ${counts[s]}`);
  if (!parts.length) return null;
  return el('p', 'muted', `أسباب تعذّر UDP: ${parts.join(' · ')}`);
}

// Under each carrier's network table: the failure line, then the family tables.
function netExtra(id, perBucket) {
  const parts = [buildFailLine(perBucket), buildFamilyTables(id, perBucket)].filter(Boolean);
  if (!parts.length) return null;
  const frag = document.createDocumentFragment();
  frag.append(...parts);
  return frag;
}

// ---------- column definitions ----------

const NET_COLS = [
  { head: 'UDP وسيط', cell: (b, f) => statCell(f['udp.p50'], fmtMs) },
  { head: 'UDP p95', cell: (b, f) => statCell(f['udp.p95'], fmtMs) },
  { head: 'الفقد', cell: (b, f) => statCell(f['udp.lossPct'], fmtPct) },
  { head: 'التذبذب', cell: (b, f) => statCell(f['udp.jitter'], fmtMs) },
  { head: 'TCP وسيط', cell: (b, f) => statCell(f['tcp.p50'], fmtMs) },
  { head: 'تنزيل', cell: (b, f) => statCell(f['down.mbps'], fmtMbps, false) },
  { head: 'رفع', cell: (b, f) => statCell(f['up.mbps'], fmtMbps, false) },
  { head: 'Cloudflare', cell: (b, f) => statCell(f['cf.p50'], fmtMs) },
  { head: 'موقع CF', cell: (b) => textCell(b.topColo ? `${b.topColo} (${b.topColoCount})` : null) },
  { head: 'UDP ممنوع', cell: (b) => textCell(isNum(b.udpBlockedPct) ? fmtPct(b.udpBlockedPct) : null) },
  { head: 'IPv6 يعمل (WebRTC)', cell: (b) => worksCell(b.ipv6WorksPct, b.ipv6Tried) },
  { head: 'IPv6 يعمل (HTTP)', cell: (b) => worksCell(b.byHttpFamily?.ipv6?.okPct, b.byHttpFamily?.ipv6?.tried) },
];

const WATCH_COLS = [
  { head: 'وسيط التأخير', cell: (b, f) => statCell(f['delay.p50'], fmtMs) },
  { head: 'p95 التأخير', cell: (b, f) => statCell(f['delay.p95'], fmtMs) },
  { head: 'أوّل إطار', cell: (b, f) => statCell(f.ttffMs, fmtMs) },
  { head: 'تقطّع/دقيقة', cell: (b, f) => statCell(f.stallsPerMin, fmtNum) },
  { head: 'على TCP', cell: (b) => textCell(isNum(b.tcpPct) ? fmtPct(b.tcpPct) : null) },
  { head: 'IPv6 يعمل', cell: (b) => worksCell(b.ipv6WorksPct, b.ipv6Tried) },
];

const HOST_COLS = [
  { head: 'الإرسال', cell: (b, f) => statCell(f.outKbpsAvg, fmtKbps) },
  { head: 'متعدّد الطبقات', cell: (b) => textCell(isNum(b.simulcastPct) ? fmtPct(b.simulcastPct) : null) },
  { head: 'على TCP', cell: (b) => textCell(isNum(b.tcpPct) ? fmtPct(b.tcpPct) : null) },
  { head: 'IPv6 يعمل', cell: (b) => worksCell(b.ipv6WorksPct, b.ipv6Tried) },
];

const TIKTOK_COLS = [
  { head: 'التأخير', cell: (b, f) => statCell(f.delayMs, fmtMs) },
];

function renderAsNames(container, asNames) {
  container.replaceChildren();
  const ids = asNames && typeof asNames === 'object'
    ? CARRIERS.map((c) => c.id).filter((id) => Array.isArray(asNames[id]) && asNames[id].length)
    : [];
  if (!ids.length) {
    container.append(el('p', 'muted', 'لا توجد بيانات بعد.'));
    return;
  }
  for (const id of ids) {
    const card = el('div', 'card');
    card.append(el('h3', null, carrierLabel(id)));
    const ul = el('ul');
    for (const a of asNames[id]) {
      const li = el('li');
      const name = a && a.asName ? String(a.asName) : '—';
      li.append(document.createTextNode(name));
      if (a && a.asn != null) li.append(document.createTextNode(` · AS${a.asn}`));
      li.append(document.createTextNode(' '));
      li.append(el('span', 'badge', String(a && isNum(a.count) ? a.count : 0)));
      ul.append(li);
    }
    card.append(ul);
    container.append(card);
  }
}

function render(data) {
  renderKind($('net'), data.nettest, NET_COLS, 'لا توجد قياسات شبكة بعد.', netExtra);
  renderKind($('watch'), data.watch, WATCH_COLS, 'لا توجد جلسات مشاهدة بعد.');
  renderKind($('host'), data.host, HOST_COLS, 'لا توجد بثوث بعد.');
  renderKind($('tiktok'), data.tiktok_manual, TIKTOK_COLS, 'لا توجد قياسات يدويّة بعد.');
  renderAsNames($('asn'), data.asNames);

  const parts = [`عدد السجلّات: ${isNum(data.totalRows) ? data.totalRows : 0}`];
  const at = data.generatedAt ? new Date(data.generatedAt) : null;
  if (at && !Number.isNaN(at.getTime())) {
    const b = new Date(at.getTime() + 3 * 3600 * 1000);
    const p2 = (n) => String(n).padStart(2, '0');
    parts.push(`آخر تحديث: ${p2(b.getUTCHours())}:${p2(b.getUTCMinutes())} بتوقيت بغداد`);
  }
  $('meta').textContent = parts.join(' · ');
}

function explain(status) {
  if (status === 401 || status === 403) return 'الرابط غير صالح. اطلب رابط التجربة من الإدارة.';
  if (status === 429) return 'طلبات كثيرة في وقت قصير. انتظر دقيقة ثمّ أعد المحاولة.';
  if (status) return 'تعذّر تحميل النتائج من الخادم. أعد المحاولة بعد قليل.';
  return 'تعذّر الوصول إلى الخادم. تحقّق من الإنترنت ثمّ أعد المحاولة.';
}

async function load() {
  if (busy) return;
  busy = true;
  const btn = $('refresh');
  btn.disabled = true;
  show($('error'), false);
  show($('loading'), true);
  try {
    const key = getKey();
    let res;
    try {
      res = await fetch('/api/results', { headers: { 'X-Lab-Key': key || '' }, cache: 'no-store' });
    } catch {
      throw Object.assign(new Error('network'), { status: 0 });
    }
    if (!res.ok) throw Object.assign(new Error('http'), { status: res.status });
    const data = await res.json();
    if (!data || typeof data !== 'object') throw Object.assign(new Error('bad'), { status: 500 });
    S.data = data;
    S.loaded = true;
    render(data);
  } catch (e) {
    const box = $('error');
    box.textContent = explain(e && e.status);
    show(box, true);
  } finally {
    show($('loading'), false);
    btn.disabled = false;
    busy = false;
  }
}

function init() {
  const gate = $('gate');
  if (!requireKeyOrExplain(gate)) {
    show(gate, true);
    show($('app'), false);
    return;
  }
  $('refresh').addEventListener('click', load);
  load();
}

init();
